import { GENERATE_AD_URL, GENERATE_AUTH_TOKEN } from '../config';

export type GenerateAdErrorType = 'policy_violation' | 'model_refusal' | 'technical';

const ERROR_COPY: Record<GenerateAdErrorType, string> = {
  policy_violation:
    'Tu imagen fue bloqueada por políticas de contenido. Esto puede deberse a contenido sensible, marcas registradas o personas reconocibles. Reformulá el pedido.',
  model_refusal:
    'El modelo no pudo generar esta imagen. Probá con un prompt más claro o reformulalo.',
  technical:
    'Tuvimos un problema técnico generando la imagen. Intentá de nuevo en un momento.',
};

export function mapErrorTypeToMessage(errorType: GenerateAdErrorType): string {
  return ERROR_COPY[errorType] ?? ERROR_COPY.technical;
}

export class GenerateAdError extends Error {
  public readonly errorType: GenerateAdErrorType;
  public readonly raw: string | null;

  constructor(errorType: GenerateAdErrorType, message: string, raw: string | null = null) {
    super(message);
    this.name = 'GenerateAdError';
    this.errorType = errorType;
    this.raw = raw;
  }

  // Errores donde reintentar con el mismo prompt no va a funcionar — el usuario
  // tiene que cambiar el pedido. Para estos NO mostramos "intentá de nuevo" ni
  // el flujo de "contactá soporte" — el problema no es nuestro, es el prompt.
  get isActionable(): boolean {
    return this.errorType === 'policy_violation' || this.errorType === 'model_refusal';
  }
}

export interface GenerateAdResponse {
  response?: string;
  error_type?: GenerateAdErrorType | null;
  error_message?: string | null;
  imagenes_generadas_ids?: Array<string | number>;
  id_company?: string;
  [key: string]: unknown;
}

/**
 * Dispara la generación de imagen contra el webhook de n8n. Devuelve el JSON
 * del response cuando hay éxito; si algo falla, lanza `GenerateAdError` con
 * el `errorType` ya clasificado (policy_violation, model_refusal o technical).
 *
 * Los callers deciden cómo mostrar el mensaje:
 *  - Si `err.isActionable` → usar `err.message` directo (sin "intentá de nuevo").
 *  - Si no → mantener su lógica actual de reintentos / "contactanos".
 */
export async function postGenerateAd(
  body: Record<string, unknown>,
  opts?: { url?: string },
): Promise<GenerateAdResponse> {
  const endpoint = opts?.url || GENERATE_AD_URL;
  let res: Response;
  try {
    res = await fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: GENERATE_AUTH_TOKEN,
      },
      body: JSON.stringify(body),
    });
  } catch (e: any) {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      e?.message ?? 'network_error',
    );
  }

  if (!res.ok) {
    let text: string | null = null;
    try {
      text = await res.clone().text();
    } catch {}
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      text || `HTTP ${res.status}`,
    );
  }

  let data: GenerateAdResponse;
  try {
    data = (await res.json()) as GenerateAdResponse;
  } catch {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      'invalid_json',
    );
  }

  // Error tipado del backend (workflow Generar anuncio V2)
  if (data?.response === 'error' && data?.error_type) {
    const t = (data.error_type as GenerateAdErrorType) || 'technical';
    throw new GenerateAdError(
      t,
      mapErrorTypeToMessage(t),
      data.error_message ?? null,
    );
  }

  // Validar respuesta exitosa esperada
  if (
    data?.response !== 'success' ||
    !Array.isArray(data?.imagenes_generadas_ids) ||
    data.imagenes_generadas_ids.length === 0
  ) {
    throw new GenerateAdError(
      'technical',
      mapErrorTypeToMessage('technical'),
      'unexpected_response',
    );
  }

  return data;
}
